import { FC } from 'react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
} from 'recharts';
import dayjs from 'dayjs';
import { ITeamCmjRsiSeriesMetric } from '../../../../models/injury';
import { ChartEmptyState } from '../../AthleteInjuryCharts/charts/_shared/ChartEmptyState';
import {
  chartAxisTick,
  chartGridProps,
  chartLegendStyle,
  chartTooltipContentStyle,
  chartTooltipItemStyle,
  chartTooltipLabelStyle,
} from '../../../../shared/charts/chartTheme';

interface Props {
  data: ITeamCmjRsiSeriesMetric | null;
  height?: number;
}

export const TeamCmjRsiSeriesChart: FC<Props> = ({ data, height }) => {
  if (!data || !data.series || data.series.length === 0) {
    return <ChartEmptyState />;
  }

  return (
    <ResponsiveContainer width="100%" height={height ?? 220}>
      <LineChart data={data.series} margin={{ top: 4, right: 16, left: -20, bottom: 0 }}>
        <CartesianGrid {...chartGridProps} />
        <XAxis
          dataKey="date"
          tickFormatter={(v) => dayjs(v).format('DD/MM')}
          tick={chartAxisTick}
        />
        <YAxis yAxisId="cmj" orientation="left" tick={chartAxisTick} />
        <YAxis yAxisId="rsi" orientation="right" tick={chartAxisTick} />
        <Tooltip
          contentStyle={chartTooltipContentStyle}
          labelStyle={chartTooltipLabelStyle}
          itemStyle={chartTooltipItemStyle}
          formatter={(value, name) => [
            value != null ? value : '–',
            name === 'cmj' ? 'CMJ media (cm)' : 'RSI medio',
          ]}
          labelFormatter={(label) => dayjs(label).format('DD/MM/YYYY')}
        />
        <Legend
          formatter={(value) => (value === 'cmj' ? 'CMJ media (cm)' : 'RSI medio')}
          wrapperStyle={chartLegendStyle}
        />
        <Line
          yAxisId="cmj"
          type="monotone"
          dataKey="cmj"
          stroke="var(--c-text-0)"
          strokeWidth={2}
          dot={{ r: 3, fill: 'var(--c-text-0)' }}
          connectNulls
        />
        {/* RSI: eccezione cyan ammessa per differenziare la seconda serie dell'asse destro */}
        <Line
          yAxisId="rsi"
          type="monotone"
          dataKey="rsi"
          stroke="#5BC9E0"
          strokeWidth={2}
          dot={{ r: 3, fill: '#5BC9E0' }}
          connectNulls
        />
        {data.benchmarks?.cmj != null && (
          <ReferenceLine
            yAxisId="cmj"
            y={data.benchmarks.cmj}
            stroke="var(--c-text-0)"
            strokeDasharray="4 3"
          />
        )}
        {data.benchmarks?.rsi != null && (
          <ReferenceLine
            yAxisId="rsi"
            y={data.benchmarks.rsi}
            stroke="#5BC9E0"
            strokeDasharray="4 3"
          />
        )}
        {data.injuryMarkers?.map((marker) => (
          <ReferenceLine
            key={marker.injuryId}
            x={marker.date}
            stroke="var(--c-error)"
            strokeWidth={1.5}
            yAxisId="cmj"
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
};
